import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import RagChatWidget from './RagChatWidget';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';

const PAGE_TITLES = {
  '/overview':        'Overview',
  '/invoices':        'Invoices',
  '/credit-notes':    'Credit Notes',
  '/exceptions':      'Exceptions',
  '/suppliers':       'Suppliers',
  '/reconciliation':  'Reconciliation',
  '/statements':      'Statements',
  '/processing-runs': 'Processing Runs',
  '/settings':        'Settings',
  '/profile':         'Profile',
  '/app-admin':             'App Admin — Organizations',
  '/app-admin/users':       'App Admin — Users',
  '/app-admin/usage':       'App Admin — Usage',
  '/app-admin/api':         'App Admin — API Usage',
  '/app-admin/logs':        'App Admin — Logs',
  '/app-admin/security':    'App Admin — Security',
  '/app-admin/configuration': 'App Admin — Configuration',
  '/app-admin/maintenance': 'App Admin — Maintenance',
};

export default function Layout({ children }) {
  const location = useLocation();
  const navigate  = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useOrg();
  const title = PAGE_TITLES[location.pathname] || 'AP Automation';
  const primaryColor = branding.primary_color || '#B71530';

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function onOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const handleSignOut = async () => {
    setOpen(false);
    await logout();
    navigate('/login');
  };

  const handleProfile = () => {
    setOpen(false);
    navigate('/profile');
  };

  const initials = (user?.display_name || user?.email || '?')[0].toUpperCase();

  return (
    <>
      {/* ── Top navbar ─────────────────────────────────────────── */}
      <nav className="navbar navbar-dark sticky-top bg-dark flex-md-nowrap p-0 shadow">
        {/* Brand */}
        <a
          className="navbar-brand col-sm-3 col-md-2 mr-0"
          href="/overview"
          style={{ letterSpacing: '0.3px' }}
        >
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.display_name}
              style={{ maxHeight: '28px', maxWidth: '140px', objectFit: 'contain' }} />
          ) : (
            <>
              <span style={{ color: primaryColor, fontWeight: 800 }}>
                {(branding.display_name || 'AP Automation').split(' ')[0]}
              </span>
              <span style={{ fontWeight: 400, fontSize: '13px', opacity: 0.9 }}>
                {' '}{(branding.display_name || 'AP Automation').split(' ').slice(1).join(' ')}
              </span>
            </>
          )}
        </a>

        {/* Page title centred */}
        <span
          className="w-100 text-center text-white d-none d-md-block"
          style={{ fontSize: '15px', fontWeight: 500, pointerEvents: 'none' }}
        >
          {title}
        </span>

        {/* User dropdown */}
        <ul className="navbar-nav px-3">
          <li className="nav-item" ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              className="nav-link btn btn-link d-flex align-items-center"
              onClick={() => setOpen(o => !o)}
              style={{
                color: 'rgba(255,255,255,.75)',
                textDecoration: 'none',
                background: 'none',
                border: 'none',
                padding: '8px 0',
                whiteSpace: 'nowrap',
              }}
            >
              {/* Avatar */}
              <div
                style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  backgroundColor: '#B71530', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: '700', marginRight: '7px', flexShrink: 0,
                }}
              >
                {initials}
              </div>
              <span style={{ fontSize: '13px' }}>{user?.display_name || user?.email}</span>
              <i
                className={`bi bi-chevron-${open ? 'up' : 'down'} ml-1`}
                style={{ fontSize: '10px', opacity: 0.7 }}
              />
            </button>

            {open && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 4px)',
                  right: 0,
                  minWidth: '210px',
                  backgroundColor: '#fff',
                  border: '1px solid rgba(0,0,0,.15)',
                  borderRadius: '.25rem',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  zIndex: 1060,
                  overflow: 'hidden',
                }}
              >
                {/* Identity header */}
                <div style={{ padding: '10px 16px 8px', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#212529' }}>
                    {user?.display_name || user?.email}
                  </div>
                  {user?.display_name && (
                    <div style={{ fontSize: '11px', color: '#6c757d' }}>{user.email}</div>
                  )}
                  <span
                    className="badge badge-secondary"
                    style={{ fontSize: '10px', marginTop: '4px', textTransform: 'capitalize' }}
                  >
                    {user?.role}
                  </span>
                </div>

                <button
                  onClick={handleProfile}
                  style={{
                    display: 'flex', alignItems: 'center', width: '100%',
                    padding: '9px 16px', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: '13px', color: '#212529', textAlign: 'left',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <i className="bi bi-person mr-2" style={{ color: '#6c757d' }} />
                  Profile
                </button>

                <div style={{ height: '1px', backgroundColor: '#f0f0f0' }} />

                <button
                  onClick={handleSignOut}
                  style={{
                    display: 'flex', alignItems: 'center', width: '100%',
                    padding: '9px 16px', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: '13px', color: '#dc3545', textAlign: 'left',
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fff5f5'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <i className="bi bi-box-arrow-right mr-2" />
                  Sign Out
                </button>
              </div>
            )}
          </li>
        </ul>
      </nav>

      {/* ── Content area ───────────────────────────────────────── */}
      <div className="container-fluid">
        <div className="row">
          <Sidebar />
          <main
            role="main"
            className="col-md-9 ml-sm-auto col-lg-10 px-4"
            style={{ paddingTop: '1.5rem', paddingBottom: '2rem' }}
          >
            {children}
          </main>
        </div>
      </div>

      <RagChatWidget />
    </>
  );
}
